import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import logo from './steelgrove-logo.png';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Markdown renderer ─────────────────────────────────────────────────────────
function renderMarkdown(text) {
  if (!text) return '';

  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
    return `<pre class="code-block"><code>${code.trim()}</code></pre>`;
  });

  html = html.replace(/`([^`\n]+)`/g, '<code class="inline-code">$1</code>');

  html = html.replace(/^### (.+)$/gm, '<h3 class="md-h3">$1</h3>');
  html = html.replace(/^## (.+)$/gm,  '<h2 class="md-h2">$1</h2>');
  html = html.replace(/^# (.+)$/gm,   '<h1 class="md-h1">$1</h1>');

  html = html.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');
  html = html.replace(/\*\*(.+?)\*\*/g,     '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g,         '<em>$1</em>');

  html = html.replace(/((\|.+\|\n?)+)/g, (match) => {
    const rows = match.trim().split('\n');
    if (rows.length < 2) return match;
    const header = rows[0];
    const isSep  = (r) => /^\|[-:\s|]+\|$/.test(r);
    let t = '<div class="table-wrap"><table class="md-table"><thead><tr>';
    header.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1)
      .forEach(cell => { t += `<th>${cell.trim()}</th>`; });
    t += '</tr></thead><tbody>';
    rows.slice(1).forEach(row => {
      if (isSep(row)) return;
      t += '<tr>';
      row.split('|').filter((_, i, arr) => i > 0 && i < arr.length - 1)
        .forEach(cell => { t += `<td>${cell.trim()}</td>`; });
      t += '</tr>';
    });
    t += '</tbody></table></div>';
    return t;
  });

  html = html.replace(/^---$/gm, '<hr class="md-hr" />');

  html = html.replace(/(^[\*\-] .+(\n|$))+/gm, (match) => {
    const items = match.trim().split('\n')
      .map(line => `<li>${line.replace(/^[\*\-] /, '').trim()}</li>`).join('');
    return `<ul class="md-ul">${items}</ul>`;
  });

  html = html.replace(/(^\d+\. .+(\n|$))+/gm, (match) => {
    const items = match.trim().split('\n')
      .map(line => `<li>${line.replace(/^\d+\. /, '').trim()}</li>`).join('');
    return `<ol class="md-ol">${items}</ol>`;
  });

  html = html.split('\n').map(line => {
    if (!line.trim()) return '';
    if (/^<(h[123]|ul|ol|li|pre|div|table|hr|thead|tbody|tr|th|td)/.test(line)) return line;
    return `<p>${line}</p>`;
  }).join('\n');

  return html;
}

// ── Spreadsheet helpers ───────────────────────────────────────────────────────
// Returns the string Excel would display in that cell, preserving number
// formatting (currency, %, commas), date formatting, etc.
function cellDisplay(cell) {
  if (!cell || cell.v == null) return '';
  // cell.w is the pre-formatted string Excel shows — use it when available
  if (cell.w !== undefined) return cell.w;
  // Fallback: format dates ourselves
  if (cell.t === 'd' && cell.v instanceof Date) {
    return cell.v.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  if (cell.t === 'b') return cell.v ? 'TRUE' : 'FALSE';
  return String(cell.v);
}

// Parses a worksheet into display-ready rows + per-column pixel widths
function parseSheet(workbook, sheetName) {
  const ws = workbook.Sheets[sheetName];
  if (!ws || !ws['!ref']) return { rows: [], colWidths: [], merges: [] };

  const range    = XLSX.utils.decode_range(ws['!ref']);
  const numRows  = range.e.r - range.s.r + 1;
  const numCols  = range.e.c - range.s.c + 1;

  const rows = [];
  for (let R = 0; R < numRows; R++) {
    const row = [];
    for (let C = 0; C < numCols; C++) {
      const addr = XLSX.utils.encode_cell({ r: range.s.r + R, c: range.s.c + C });
      const cell = ws[addr];
      row.push(cell
        ? { display: cellDisplay(cell), isNum: cell.t === 'n' }
        : { display: '', isNum: false }
      );
    }
    rows.push(row);
  }

  // Map Excel column widths to pixels (wch = chars, wpx = pixels)
  const colWidths = Array.from({ length: numCols }, (_, i) => {
    const col = ws['!cols']?.[range.s.c + i];
    if (!col) return null;
    if (col.wpx) return Math.max(col.wpx, 60);
    if (col.wch != null) return Math.max(Math.round(col.wch * 7), 60);
    return null;
  });

  // Merged cell ranges expressed as relative row/col indices so the component
  // doesn't need to know about the worksheet's absolute address offset.
  const merges = (ws['!merges'] || []).flatMap(m => {
    const r1 = m.s.r - range.s.r;
    const c1 = m.s.c - range.s.c;
    const r2 = m.e.r - range.s.r;
    const c2 = m.e.c - range.s.c;
    if (r1 < 0 || c1 < 0) return []; // starts outside our view
    return [{
      r1, c1,
      r2: Math.min(r2, numRows - 1),
      c2: Math.min(c2, numCols - 1),
    }];
  });

  return { rows, colWidths, merges };
}

// ── SpreadsheetPanel ──────────────────────────────────────────────────────────
function SpreadsheetPanel({ spreadsheet, onSwitch, onRemove }) {
  const { fileName, sheetNames, activeSheet, rows, colWidths, merges = [] } = spreadsheet;
  const headerRow = rows[0] || [];
  const dataRows  = rows.slice(1);

  // Build two lookup structures from the merges array:
  //   spanAt["r,c"] = { rowSpan, colSpan } — the top-left cell of each merge
  //   skip          = Set of "r,c" keys for covered (non-top-left) merge cells
  const { spanAt, skip } = useMemo(() => {
    const spanAt = Object.create(null);
    const skip   = new Set();
    for (const { r1, c1, r2, c2 } of (merges || [])) {
      spanAt[`${r1},${c1}`] = { rowSpan: r2 - r1 + 1, colSpan: c2 - c1 + 1 };
      for (let r = r1; r <= r2; r++)
        for (let c = c1; c <= c2; c++)
          if (r !== r1 || c !== c1) skip.add(`${r},${c}`);
    }
    return { spanAt, skip };
  }, [merges]);

  return (
    <div className="spreadsheet-panel">
      {/* toolbar */}
      <div className="sheet-toolbar">
        <div className="sheet-file-info">
          <svg className="sheet-file-icon" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2"/>
            <path d="M3 9h18M3 15h18M9 3v18"/>
          </svg>
          <span className="sheet-filename" title={fileName}>{fileName}</span>
          <button className="sheet-remove-btn" onClick={onRemove} title="Remove file">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
            Remove
          </button>
        </div>

        {sheetNames.length > 1 && (
          <div className="sheet-tabs">
            {sheetNames.map(name => (
              <button
                key={name}
                className={`sheet-tab${name === activeSheet ? ' active' : ''}`}
                onClick={() => onSwitch(name)}
              >
                {name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* grid */}
      <div className="sheet-grid-wrap">
        {rows.length === 0 ? (
          <p className="sheet-empty">No data found in this sheet.</p>
        ) : (
          <table className="sheet-grid">
            <colgroup>
              <col style={{ width: '42px', minWidth: '42px' }} />
              {headerRow.map((_, i) => (
                <col key={i} style={
                  colWidths[i]
                    ? { width: `${colWidths[i]}px`, minWidth: '60px' }
                    : { minWidth: '80px' }
                } />
              ))}
            </colgroup>

            <thead>
              <tr>
                <th className="row-num-col" />
                {headerRow.map((h, i) => {
                  if (skip.has(`0,${i}`)) return null; // covered by a merge
                  const span = spanAt[`0,${i}`] || {};
                  return (
                    <th
                      key={i}
                      colSpan={span.colSpan || 1}
                      rowSpan={span.rowSpan || 1}
                      style={{ textAlign: h.isNum ? 'right' : 'left' }}
                    >
                      {h.display}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {dataRows.map((row, ri) => {
                const rowIdx = ri + 1; // +1 because header is row 0
                return (
                  <tr key={ri}>
                    <td className="row-num-col">{ri + 1}</td>
                    {row.map((cell, ci) => {
                      if (skip.has(`${rowIdx},${ci}`)) return null; // covered by a merge
                      const span = spanAt[`${rowIdx},${ci}`] || {};
                      return (
                        <td
                          key={ci}
                          colSpan={span.colSpan || 1}
                          rowSpan={span.rowSpan || 1}
                          style={{ textAlign: cell.isNum ? 'right' : 'left' }}
                          title={cell.display}
                        >
                          {cell.display}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Auth Screen ───────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Authentication failed');
      onAuth(data.token, data.name);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-logo-wrap">
          <img src={logo} alt="Steel Grove" className="auth-logo" />
        </div>
        <h1 className="auth-title">Steel Grove Capital Advisors</h1>
        <p className="auth-subtitle">Investment Intelligence</p>
        <form onSubmit={handleSubmit} className="auth-form">
          <div className="field-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@steelgrovecapital.com"
              required
            />
          </div>
          <div className="field-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? <span className="btn-spinner" /> : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Chat Screen ───────────────────────────────────────────────────────────────
const CHAT_WIDTH_DEFAULT = 400;
const CHAT_WIDTH_MIN     = 280;
const CHAT_WIDTH_MAX     = 680;

function ChatScreen({ token, userName, onLogout }) {
  // Wraps fetch: injects auth header + redirects to login on 401
  const apiFetch = useCallback(async (url, options = {}) => {
    const res = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });
    if (res.status === 401) {
      onLogout();
      throw new Error('Session expired. Please sign in again.');
    }
    return res;
  }, [token, onLogout]);

  const [messages, setMessages]       = useState([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [model, setModel]             = useState('sonnet');
  const [error, setError]             = useState('');
  const [image, setImage]             = useState(null);
  const [spreadsheet, setSpreadsheet] = useState(null);
  const [chatWidth, setChatWidth]     = useState(CHAT_WIDTH_DEFAULT);

  const bottomRef    = useRef(null);
  const textareaRef  = useRef(null);
  const fileInputRef = useRef(null);
  const isResizing   = useRef(false);
  const resizeStartX = useRef(0);
  const resizeStartW = useRef(CHAT_WIDTH_DEFAULT);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Resize drag handlers (attached to window to track mouse outside the handle)
  useEffect(() => {
    function onMouseMove(e) {
      if (!isResizing.current) return;
      const dx = resizeStartX.current - e.clientX; // drag left = wider chat
      setChatWidth(Math.max(CHAT_WIDTH_MIN, Math.min(CHAT_WIDTH_MAX, resizeStartW.current + dx)));
    }
    function onMouseUp() {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor     = '';
        document.body.style.userSelect = '';
      }
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);

  function onHandleMouseDown(e) {
    isResizing.current  = true;
    resizeStartX.current = e.clientX;
    resizeStartW.current = chatWidth;
    document.body.style.cursor     = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  }

  function autoResize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  // ── File handling ────────────────────────────────────────────────────────────
  function loadSpreadsheetFile(file) {
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const bytes    = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(bytes, { type: 'array', cellDates: true });
        const sheetNames  = workbook.SheetNames;
        const activeSheet = sheetNames[0];
        const { rows, colWidths, merges } = parseSheet(workbook, activeSheet);

        // Build base64 for backend — chunked spread is faster than byte-by-byte concat
        // and avoids call-stack limits on large files
        const CHUNK = 8192;
        let binary = '';
        for (let i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
        }
        const base64 = btoa(binary);

        setSpreadsheet({ base64, fileName: file.name, workbook, sheetNames, activeSheet, rows, colWidths, merges });
      } catch (err) {
        setError(`Could not read "${file.name}": ${err.message}`);
      }
    };
    reader.onerror = () => setError(`Failed to read "${file.name}".`);
    reader.readAsArrayBuffer(file);
  }

  function switchSheet(sheetName) {
    if (!spreadsheet) return;
    try {
      const { rows, colWidths, merges } = parseSheet(spreadsheet.workbook, sheetName);
      setSpreadsheet(prev => ({ ...prev, activeSheet: sheetName, rows, colWidths, merges }));
    } catch (err) {
      setError(`Could not load sheet "${sheetName}": ${err.message}`);
    }
  }

  function removeSpreadsheet() { setSpreadsheet(null); }

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.name.match(/\.(csv|xlsx|xls)$/i)) {
      loadSpreadsheetFile(file);
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result;
        setImage({ base64: dataUrl.split(',')[1], type: file.type, preview: dataUrl, fileName: file.name });
      };
      reader.readAsDataURL(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeImage() {
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // ── Send message ─────────────────────────────────────────────────────────────
  async function sendMessage() {
    const text = input.trim();
    if ((!text && !image) || loading) return;

    const userMsg = {
      role: 'user',
      content: text || 'Please analyze this image.',
      imagePreview: image?.preview || null,
    };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setError('');
    setLoading(true);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const capturedImage = image;
    setImage(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    try {
      const history = newMessages.slice(0, -1).map(m => ({ role: m.role, content: m.content }));
      const body = {
        message: text || 'Please analyze this image.',
        history,
        model,
        ...(capturedImage && { image: capturedImage.base64, image_type: capturedImage.type }),
        ...(spreadsheet && { file_data: spreadsheet.base64, file_name: spreadsheet.fileName }),
      };
      const res = await apiFetch(`${API_URL}/query`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  const modelLabel = model === 'sonnet' ? 'Sonnet 4.6' : 'Opus 4';
  const canSend    = (!!input.trim() || !!image) && !loading;

  return (
    <div className={`chat-screen${spreadsheet ? ' has-spreadsheet' : ''}`}>

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className="sidebar">
        <div className="sidebar-top">
          <img src={logo} alt="SG" className="sidebar-logo" />
          <span className="sidebar-brand">Steel Grove</span>
        </div>
        <nav className="sidebar-nav">
          <button className="nav-item active">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z"/>
            </svg>
            <span>Advisor</span>
          </button>
          <button className="nav-item" onClick={() => { setMessages([]); setError(''); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 4v16m8-8H4"/>
            </svg>
            <span>New Chat</span>
          </button>
        </nav>
        <div className="sidebar-bottom">
          <div className="model-section">
            <p className="model-label">Model</p>
            <div className="model-toggle">
              <button className={`model-btn ${model === 'sonnet' ? 'active' : ''}`} onClick={() => setModel('sonnet')}>Sonnet 4.6</button>
              <button className={`model-btn ${model === 'opus'   ? 'active' : ''}`} onClick={() => setModel('opus')}>Opus 4</button>
            </div>
          </div>
          <div className="user-row">
            <div className="user-avatar">{userName.charAt(0).toUpperCase()}</div>
            <span className="user-name">{userName}</span>
            <button className="logout-btn" onClick={onLogout} title="Sign out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
              </svg>
            </button>
          </div>
        </div>
      </aside>

      {/* ── Spreadsheet panel ────────────────────────────────────────────────── */}
      {spreadsheet && (
        <SpreadsheetPanel
          spreadsheet={spreadsheet}
          onSwitch={switchSheet}
          onRemove={removeSpreadsheet}
        />
      )}

      {/* ── Drag handle (only shown when a file is loaded) ───────────────────── */}
      {spreadsheet && (
        <div className="split-handle" onMouseDown={onHandleMouseDown} title="Drag to resize">
          <div className="split-handle-grip">
            {/* left + right chevrons */}
            <svg viewBox="0 0 16 20" fill="none" stroke="currentColor"
                 strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="5,4 1,10 5,16" />
              <polyline points="11,4 15,10 11,16" />
            </svg>
          </div>
        </div>
      )}

      {/* ── Chat panel ──────────────────────────────────────────────────────── */}
      <main
        className="chat-main"
        style={spreadsheet ? { flex: `0 0 ${chatWidth}px` } : undefined}
      >
        <div className="chat-header">
          <div>
            <h2 className="chat-title">Investment Advisor</h2>
            <span className="chat-model-badge">{modelLabel}</span>
          </div>
          {spreadsheet && (
            <div className="chat-file-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M3 9h18M3 15h18M9 3v18"/>
              </svg>
              <span>{spreadsheet.fileName}</span>
            </div>
          )}
        </div>

        <div className="messages-area">
          {messages.length === 0 && (
            <div className="empty-state">
              <img src={logo} alt="SG" className="empty-logo" />
              <h3>How can I help you today?</h3>
              <p>
                {spreadsheet
                  ? `${spreadsheet.fileName} is loaded — ask anything about this data.`
                  : 'Ask about deal analysis, cap rates, market trends, or upload a spreadsheet or image for analysis.'}
              </p>
              <div className="suggestion-chips">
                {(spreadsheet
                  ? ['Summarize this data', 'What are the key metrics?', 'Which property has the highest cap rate?', 'Identify any red flags']
                  : ['Analyze a value-add multifamily deal', 'Explain cap rate compression', 'Walk me through a 1031 exchange', 'How do I evaluate a distressed asset opportunity?']
                ).map(s => (
                  <button key={s} className="chip" onClick={() => { setInput(s); textareaRef.current?.focus(); }}>{s}</button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`message-row ${msg.role}`}>
              {msg.role === 'assistant' && (
                <div className="msg-avatar assistant-avatar"><img src={logo} alt="SG" /></div>
              )}
              <div className={`bubble ${msg.role}`}>
                {msg.imagePreview && (
                  <img src={msg.imagePreview} alt="uploaded" className="msg-image-preview" />
                )}
                {msg.role === 'assistant'
                  ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                  : <span>{msg.content}</span>
                }
              </div>
              {msg.role === 'user' && (
                <div className="msg-avatar user-avatar">{userName.charAt(0).toUpperCase()}</div>
              )}
            </div>
          ))}

          {loading && (
            <div className="message-row assistant">
              <div className="msg-avatar assistant-avatar"><img src={logo} alt="SG" /></div>
              <div className="bubble assistant typing-bubble">
                <span className="dot" /><span className="dot" /><span className="dot" />
              </div>
            </div>
          )}

          {error && <div className="error-banner">{error}</div>}
          <div ref={bottomRef} />
        </div>

        {/* Image preview (images only — spreadsheet shows in its own panel) */}
        {image && (
          <div className="image-preview-bar">
            <img src={image.preview} alt="preview" className="input-img-thumb" />
            <span className="img-name">{image.fileName}</span>
            <button className="remove-img" onClick={removeImage}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        )}

        <div className="input-bar">
          <input
            type="file"
            accept="image/*,.csv,.xlsx,.xls"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            className="attach-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Attach image or spreadsheet"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={e => { setInput(e.target.value); autoResize(); }}
            onKeyDown={handleKeyDown}
            placeholder={spreadsheet ? `Ask about ${spreadsheet.fileName}…` : 'Ask about a deal, a market, an investment strategy…'}
            rows={1}
            disabled={loading}
          />
          <button
            className={`send-btn ${canSend ? '' : 'disabled'}`}
            onClick={sendMessage}
            disabled={!canSend}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </div>
      </main>

    </div>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────
export default function App() {
  const [token, setToken]       = useState(() => localStorage.getItem('sg_token') || '');
  const [userName, setUserName] = useState(() => localStorage.getItem('sg_name') || '');
  const [validating, setValidating] = useState(() => !!localStorage.getItem('sg_token'));

  const handleLogout = useCallback(() => {
    localStorage.removeItem('sg_token');
    localStorage.removeItem('sg_name');
    setToken('');
    setUserName('');
  }, []);

  function handleAuth(t, name) {
    localStorage.setItem('sg_token', t);
    localStorage.setItem('sg_name', name);
    setToken(t);
    setUserName(name);
  }

  useEffect(() => {
    const storedToken = localStorage.getItem('sg_token');
    if (!storedToken) { setValidating(false); return; }

    fetch(`${API_URL}/auth/me`, {
      headers: { 'Authorization': `Bearer ${storedToken}` },
    })
      .then(res => {
        if (res.status === 401) {
          handleLogout();
        } else if (res.ok) {
          return res.json().then(data => {
            if (data.name) {
              localStorage.setItem('sg_name', data.name);
              setUserName(data.name);
            }
          });
        }
      })
      .catch(() => {})
      .finally(() => setValidating(false));
  }, [handleLogout]);

  if (validating) return <div className="auth-screen" />;
  if (!token)     return <AuthScreen onAuth={handleAuth} />;
  return <ChatScreen token={token} userName={userName} onLogout={handleLogout} />;
}
